<script lang="ts" generics="T extends object">
  import AwaitItemsResponse from "/@lib/components/loading/AwaitItemsResponse.svelte"
  import {
    FlexRender,
    createSvelteTable,
  } from "/@lib/components/table/svelte-table"
  import type { ColumnDefinition, ColumnDefinitions } from "/@lib/types/Table"
  import {
    getCoreRowModel,
    getSortedRowModel,
    type ColumnDef,
  } from "@tanstack/table-core"
  import { Table, Tooltip, cn } from "@tastyworks/ui-library"
  import { untrack, type Snippet } from "svelte"
  import ScrollShadow from "./ScrollShadow.svelte"
  import SimpleTable from "./SimpleTable.svelte"
  import { createColumnDrag } from "./column-drag.svelte"
  import {
    autofitColumn as autofitColumnImpl,
    loadPersisted,
    reconcileColumnOrder,
    type PersistedLayout,
  } from "./column-layout"

  type Props = {
    items: T[]
    columns: ColumnDefinitions
    resizable?: boolean
    reorderable?: boolean
    class?: string
    onItemSelected?: (item: T) => void
    onItemDoubleClicked?: (item: T) => void
    storageKey?: string
    /** Distribute leftover space among columns (basis-proportional) so a
     *  table with few columns doesn't leave a gap on the right. */
    fillWidth?: boolean
    /** Opt-in fixed-width cell pinned at the far left of every row, with
     *  an empty matching header cell. Designed for non-data status
     *  indicators (e.g. a padlock icon for locked users) — never sorts,
     *  drags, or resizes, since it lives outside the columns array. */
    leadingCell?: Snippet<[T]>
  }

  let {
    items,
    columns,
    resizable = true,
    reorderable = resizable,
    class: className = "",
    onItemSelected,
    onItemDoubleClicked,
    storageKey,
    fillWidth = false,
    leadingCell,
  }: Props = $props()

  let selectedItem = $state.raw<T | null>(null)

  const persisted = untrack(() => loadPersisted(storageKey))
  const initialColumnOrder = untrack(() =>
    reconcileColumnOrder(
      persisted.columnOrder,
      columns.map((c) => c.name)
    )
  )

  const columnByName = $derived(new Map(columns.map((c) => [c.name, c])))

  const tableColumns = $derived.by<ColumnDef<T>[]>(() => {
    return columns.map((col) => ({
      id: col.name,
      header: col.name,
      enableSorting: col.sortable !== false,
      enableResizing: resizable,
      // accessorFn is required for column.getCanSort() to return true.
      accessorFn: (row) =>
        col.comparator ? col.comparator(row) : col.dataFormatter(row),
      // Body markup branches on the original ColumnDefinition (link /
      // popup / default) — this fallback only runs if it doesn't.
      cell: ({ row }) => col.dataFormatter(row.original) ?? "",
      // See TreeTable for why ascending-first must be forced.
      sortDescFirst: false,
    }))
  })

  const table = createSvelteTable<T>({
    get data() {
      return items
    },
    get columns() {
      return tableColumns
    },
    initialState: {
      columnSizing: persisted.columnSizing ?? {},
      columnOrder: initialColumnOrder,
      sorting: persisted.sorting ?? [],
    },
    onStateChange: () => {
      if (!storageKey) return
      queueMicrotask(() => {
        const s = table.getState()
        const snapshot: PersistedLayout = {
          columnSizing: { ...(s.columnSizing ?? {}) },
          columnOrder: [...(s.columnOrder ?? [])],
          sorting: [...(s.sorting ?? [])],
        }
        try {
          localStorage.setItem(storageKey, JSON.stringify(snapshot))
        } catch {
          /* quota / private mode */
        }
      })
    },
    enableColumnResizing: resizable,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  // See TreeTable for why we touch state slices inside these deriveds.
  const headerGroups = $derived.by(() => {
    void table.state.sorting
    void table.state.columnSizing
    void table.state.columnOrder
    return table.getHeaderGroups()
  })

  // table-core caches `getRowModel().rows` and the row instances themselves
  // across columnOrder updates, and `row.getVisibleCells()` inside the
  // template is opaque to Svelte's reactivity. Materializing both layers
  // in a derived gives Svelte fresh array references to diff against, so
  // the body reorders alongside the header.
  const rowsWithCells = $derived.by(() => {
    void table.state.sorting
    void table.state.columnSizing
    void table.state.columnOrder
    return table.getRowModel().rows.map((row) => ({
      row,
      cells: row.getVisibleCells(),
    }))
  })

  const drag = createColumnDrag({
    getColumnOrder: () => table.state.columnOrder ?? [],
    setColumnOrder: (order) => table.setColumnOrder(order),
  })

  function autofitColumn(name: string, e: Event) {
    const root = (e.target as HTMLElement).closest(
      "[role='table']"
    ) as HTMLElement | null
    if (!root) return
    const width = autofitColumnImpl({
      rootElement: root,
      visibleColumns: table.getVisibleLeafColumns(),
      columns,
      items,
      columnId: name,
    })
    if (width === null) return
    table.setColumnSizing((prev) => ({ ...prev, [name]: width }))
  }

  const DEFAULT_COLUMN_WIDTH = 150

  // Width of the optional leading status-icon cell. Sized for a single
  // small status icon; the cell neutralizes the parent's first-child left
  // pad (see `!pl-0` below) so the glyph sits centered in this width.
  const LEADING_CELL_WIDTH_PX = 28

  function widthStyle(name: string): string {
    const w = table.state.columnSizing?.[name]
    const px = w ?? DEFAULT_COLUMN_WIDTH
    return fillWidth
      ? `flex: ${px} 0 ${px}px`
      : `width: ${px}px; flex: 0 0 auto`
  }

  function originalColumn(name: string): ColumnDefinition | undefined {
    return columnByName.get(name)
  }
</script>

<ScrollShadow class={cn("h-full", className)}>
  <Table.Root class="flex h-full flex-col overflow-auto border-2">
    <div
      role="rowgroup"
      class="header text-general-default-text sticky top-0 z-[1] flex w-fit min-w-full leading-4 [&>*:first-child]:pl-2"
    >
      {#if leadingCell}
        <Table.HeaderCell
          span={1}
          style="width: {LEADING_CELL_WIDTH_PX}px; flex: 0 0 auto"
          class="border-divider-horizontal-end flex border-r border-b-[1px] border-solid pl-0!"
          data-testid="leading-cell-header"
        >
          <!-- Status-icon column: intentionally headerless and inert. -->
        </Table.HeaderCell>
      {/if}
      {#each headerGroups as headerGroup}
        {#each headerGroup.headers as header}
          {@const col = header.column}
          {@const name = col.id}
          {@const sortable = col.getCanSort()}
          {@const sortEntry = (table.state.sorting ?? []).find(
            (s) => s.id === name
          )}
          {@const sortDir = sortEntry
            ? sortEntry.desc
              ? "desc"
              : "asc"
            : false}
          <Table.HeaderCell
            class="border-trade-desk-gray-dark relative flex border-r-2 border-b-[1px] border-solid px-1 {drag.columnClasses(
              name
            )}"
            span={1}
            style={widthStyle(name)}
          >
            <button
              type="button"
              class="flex min-w-0 flex-1 justify-between {reorderable
                ? 'cursor-move'
                : ''} {sortable ? '' : 'cursor-default'}"
              draggable={reorderable}
              onclick={col.getToggleSortingHandler()}
              ondragstart={(e) => drag.onDragStart(e, name)}
              ondragover={(e) => drag.onDragOver(e, name)}
              ondragleave={() => drag.onDragLeave(name)}
              ondrop={(e) => drag.onDrop(e, name)}
              ondragend={drag.cleanupDrag}
            >
              <span class="min-w-0 flex-1 truncate">
                <FlexRender
                  content={header.column.columnDef.header}
                  context={header.getContext()}
                />
              </span>
              <span class="shrink-0">
                {#if sortable}
                  {sortDir === "asc" ? "▲" : sortDir === "desc" ? "▼" : ""}
                {/if}
              </span>
            </button>
            {#if resizable && col.getCanResize()}
              <div
                class="group absolute inset-y-0 -right-2 z-10 w-4 hover:cursor-col-resize"
                role="button"
                tabindex={0}
                aria-roledescription="column width editor"
                aria-label="Resize {name} column"
                data-testid="column-resizer-{name}"
                onmousedown={header.getResizeHandler()}
                ondblclick={(e) => autofitColumn(name, e)}
                onkeydown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    autofitColumn(name, e)
                  }
                }}
              >
                <div
                  class="pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 group-hover:bg-blue-500/60"
                ></div>
              </div>
            {/if}
          </Table.HeaderCell>
        {/each}
      {/each}
    </div>

    <Table.Body class="min-h-0 flex-1">
      {#each rowsWithCells as { row, cells }, i}
        {@const item = row.original}
        <div
          role="row"
          tabindex={0}
          aria-rowindex={i + 1}
          class="body-row border-divider-horizontal-end flex w-fit min-w-full border-b leading-10 hover:cursor-pointer [&>*:first-child]:pl-2"
          class:text-white={selectedItem === item}
          class:bg-blue-600={selectedItem === item}
          onfocus={() => {
            selectedItem = item
            onItemSelected?.(item)
          }}
          ondblclick={() => onItemDoubleClicked?.(item)}
        >
          {#if leadingCell}
            <Table.BodyCell
              span={1}
              style="width: {LEADING_CELL_WIDTH_PX}px; flex: 0 0 auto"
              class="border-divider-horizontal-end flex items-center justify-center border-r border-solid pl-0!"
              data-testid="leading-cell-body"
            >
              {@render leadingCell(item)}
            </Table.BodyCell>
          {/if}
          {#each cells as cell}
            {@const name = cell.column.id}
            {@const orig = originalColumn(name)}
            {#if orig?.linkFormatter}
              <Table.BodyCell
                span={1}
                style={widthStyle(name)}
                class={cn(
                  "border-trade-desk-gray-dark border-r-2 border-solid px-1 text-blue-600",
                  orig.style,
                  drag.columnClasses(name)
                )}
              >
                <a
                  href={orig.linkFormatter(item)}
                  target="_blank"
                  rel="noopener noreferrer"
                  class:text-white={selectedItem === item}
                >
                  {orig.dataFormatter(item) ?? ""}
                </a>
              </Table.BodyCell>
            {:else if orig?.popupTableFormatter}
              {@const popup = orig.popupTableFormatter}
              <Table.BodyCell
                span={1}
                style={widthStyle(name)}
                class={cn(
                  "border-trade-desk-gray-dark border-r-2 border-solid px-1 text-blue-600",
                  orig.style,
                  drag.columnClasses(name)
                )}
              >
                <Tooltip.Root delayDuration={20}>
                  <Tooltip.Trigger
                    type="button"
                    onclick={() => {
                      if (selectedItem !== item) {
                        selectedItem = item
                        onItemSelected?.(item)
                      }
                    }}
                  >
                    <p
                      class:text-white={selectedItem === item}
                      class="text-sm text-balance underline"
                    >
                      {orig.dataFormatter(item) ?? ""}
                    </p>
                  </Tooltip.Trigger>
                  {#if selectedItem === item}
                    <Tooltip.Content class="p-2">
                      <AwaitItemsResponse
                        promise={popup.fetchItemInfo(item)}
                        noResultsMessage="No events found"
                      >
                        {#snippet children({ items }: { items: T[] })}
                          <SimpleTable columns={popup.columns} {items} />
                        {/snippet}
                      </AwaitItemsResponse>
                    </Tooltip.Content>
                  {/if}
                </Tooltip.Root>
              </Table.BodyCell>
            {:else}
              <Table.BodyCell
                title={orig?.dataFormatter(item)}
                span={1}
                style={widthStyle(name)}
                class={cn(
                  "border-trade-desk-gray-dark border-r-2 border-solid px-1",
                  orig?.style,
                  drag.columnClasses(name)
                )}
              >
                {orig?.dataFormatter(item) ?? ""}
              </Table.BodyCell>
            {/if}
          {/each}
        </div>
      {/each}
    </Table.Body>
  </Table.Root>
</ScrollShadow>
